import React, { useEffect, useState } from "react";
import { FiMinus, FiPlus, FiTrash2, FiLock } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addToBuyCart,
  addToRentCart,
  decreaseBuyQty,
  decreaseRentQty,
  removeFromBuyCart,
  removeFromRentCart,
} from "../../redux/cartSlice";
import axiosInstance from "../../utils/axiosInstance";
import LoginModal from "../Login/Login.jsx"
import AddressModal from "./AddressModal.jsx";
import Breadcrumbs from "../Breadcrumbs.jsx";
import "./cartpage.css";

const formatMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "Quote on request";
  return `$${number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getLinePrice = (item) => {
  // Prefer API-calculated offerPrice (from rental_charges final_cost) over raw package price
  if (item.offerPrice != null) {
    return Number(item.offerPrice);
  }
  if (item.selectedPackage && item.selectedPackage.offer_price) {
    return Number(item.selectedPackage.offer_price);
  }
  return Number(item.sale_offer_price ?? item.sale_price ?? item.oldPrice ?? item.rental_price ?? 0);
};

const getOriginalPrice = (item) => {
  // Prefer API-calculated base_cost as the original price
  if (item.rentalCharges?.base_cost) {
    return Number(item.rentalCharges.base_cost);
  }
  if (item.rental_price != null) {
    return Number(item.rental_price);
  }
  if (item.selectedPackage?.price) {
    return Number(item.selectedPackage.price);
  }
  return Number(item.oldPrice ?? item.sale_price ?? getLinePrice(item));
};

function Cartpage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { buyCart = [], rentCart = [] } = useSelector((store) => store.cart || {});
  const totalItems = buyCart.length + rentCart.length;
  const totalAmount = [...buyCart, ...rentCart].reduce(
    (sum, item) => sum + getLinePrice(item) * (item.qty || 1),
    0
  );

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem("token"));
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [refreshAdd, setRefreshAdd] = useState(false);

  const defaultAddrStr = localStorage.getItem("defaultAddress");
  const defaultAddr = defaultAddrStr ? JSON.parse(defaultAddrStr) : null;
  const saleAddress = localStorage.getItem("saleAddress") ? JSON.parse(localStorage.getItem("saleAddress")) : defaultAddr;
  const rentalAddress = localStorage.getItem("rentalAddress") ? JSON.parse(localStorage.getItem("rentalAddress")) : defaultAddr;
  const unifiedAddress = saleAddress || rentalAddress;

  const fetchCouponsAndNavigate = async () => {
    let saleCoupons = [];
    let rentalCoupons = [];
    try {
      const [saleRes, rentalRes] = await Promise.allSettled([
        axiosInstance.get("catlog/coupons/?coupon_type=sale&is_suspended=false"),
        axiosInstance.get("catlog/coupons/?coupon_type=rental&is_suspended=false"),
      ]);
      if (saleRes.status === "fulfilled") saleCoupons = saleRes.value?.data?.results || [];
      if (rentalRes.status === "fulfilled") rentalCoupons = rentalRes.value?.data?.results || [];
    } catch (_) { /* proceed without coupons */ }
    navigate("/checkout", { state: { saleCoupons, rentalCoupons } });
  };

  const handleCheckoutClick = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoggedIn(false);
      setShowLoginModal(true);
      return;
    }
    if (!unifiedAddress && totalItems > 0) {
      toast.error("Please select a delivery address");
      setShowAddressModal(true);
      return;
    }

    if (unifiedAddress) {
      if (!localStorage.getItem("saleAddress") && buyCart.length > 0) {
        localStorage.setItem("saleAddress", JSON.stringify(unifiedAddress));
      }
      if (!localStorage.getItem("rentalAddress") && rentCart.length > 0) {
        localStorage.setItem("rentalAddress", JSON.stringify(unifiedAddress));
      }
    }

    fetchCouponsAndNavigate();
  };

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    setShowLoginModal(false);

    const currentSaleAddress = localStorage.getItem("saleAddress") ? JSON.parse(localStorage.getItem("saleAddress")) : null;
    const currentRentalAddress = localStorage.getItem("rentalAddress") ? JSON.parse(localStorage.getItem("rentalAddress")) : null;
    const currentUnified = currentSaleAddress || currentRentalAddress;

    if (!currentUnified && totalItems > 0) {
      toast.error("Please select a delivery address");
      setShowAddressModal(true);
    } else {
      if (currentUnified) {
        if (!localStorage.getItem("saleAddress") && buyCart.length > 0) {
          localStorage.setItem("saleAddress", JSON.stringify(currentUnified));
        }
        if (!localStorage.getItem("rentalAddress") && rentCart.length > 0) {
          localStorage.setItem("rentalAddress", JSON.stringify(currentUnified));
        }
      }
      fetchCouponsAndNavigate();
    }
  };

  useEffect(() => {
    // 2: Call site-config API immediately on user move to cartpage
    axiosInstance.get(`sitedata/site-config/`).catch(() => axiosInstance.get(`sitedata/site/`)).catch(() => { });
  }, []);

  const openProduct = (item) => {
    if (item.slug || item.friendlyurl) {
      navigate(`/product/${item.slug || item.friendlyurl}`, {
        search: item.id ? `variant=${item.id}` : "",
        state: {
          variantId: item.id,
          type: item.type, // "buy" or "rent"
        },
      });
    }
  };

  const groupedItems = [];
  [
    ...buyCart.map(i => ({ ...i, type: "buy" })),
    ...rentCart.map(i => ({ ...i, type: "rent" }))
  ].forEach(item => {
    let group = groupedItems.find(g => g.id === item.id);
    if (!group) {
      group = { id: item.id, name: item.name, image: item.image, items: [] };
      groupedItems.push(group);
    }
    group.items.push(item);
  });

  const handleRemoveGroup = (groupItems) => {
    groupItems.forEach(item => {
      const key = item.type === "rent" ? item.cartItemId || item.id : item.id;
      const removeAction = item.type === "rent" ? removeFromRentCart : removeFromBuyCart;
      dispatch(removeAction(key));
    });
    toast.success("Items removed from cart");
  };

  return (
    <main className="cart-page template-construction">
      <div className="cart-container">
        <Breadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Cart", path: "" }
        ]} />
      </div>
      {totalItems > 0 ? (
        <div className="cart-container">
          <section className="cart-left">


            {unifiedAddress ? (
              <div className="cart-section-box cart-delivery-estimate mb-4">
                <div className="cart-delivery-estimate-left">
                  <div>
                    <p className="deliver-address">Delivering to: <span>{unifiedAddress?.name || ""}</span></p>
                    <p className="delivery-full-add">
                      Flat / House No : {unifiedAddress.flat_no}, {unifiedAddress.address_line_1}, {unifiedAddress.address_line_2}, {unifiedAddress.landmark}, {unifiedAddress.city}, {unifiedAddress.state}, {unifiedAddress.country}
                    </p>
                  </div>
                </div>
                <div className="cart-delivery-estimate-right">
                  <button type="button" onClick={() => setShowAddressModal(true)} className="change-address-btn">
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <div className="cart-section-box cart-delivery-estimate mb-4">
                <div className="cart-delivery-estimate-left">
                  <p className="delivery-detail" style={{ margin: 0, fontWeight: 700 }}>
                    Select Delivery Address
                  </p>
                </div>
                <div className="cart-delivery-estimate-right">
                  {isLoggedIn ? (
                    <button type="button" onClick={() => setShowAddressModal(true)} className="change-address-btn">
                      Choose
                    </button>
                  ) : (
                    <button type="button" onClick={() => setShowLoginModal(true)} className="change-address-btn">
                      Choose
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="cart-sticky-sections">
              {groupedItems.map(group => (
                <div className="cart-product-group" key={group.id}>
                  <div className="cart-product-header">
                    <div className="d-flex align-items-center">
                      <button className="group-img-btn" type="button" onClick={() => openProduct(group.items[0])}>
                        <img src={group.image} alt={group.name} className="group-image" />
                      </button>
                      <div className="group-info ms-3">
                        <h3 className="group-title cursor-pointer" onClick={() => openProduct(group.items[0])}>{group.name}</h3>
                        <span className="group-config-count">Configurations ({group.items.length})</span>
                      </div>
                    </div>
                    <button className="group-remove-all" onClick={() => handleRemoveGroup(group.items)}>Remove All</button>
                  </div>
                  <div className="cart-product-items">
                    {group.items.map(item => {
                      const key = item.type === "rent" ? item.cartItemId || item.id : item.id;
                      const removeAction = item.type === "rent" ? removeFromRentCart : removeFromBuyCart;
                      const decrease = item.type === "rent" ? decreaseRentQty : decreaseBuyQty;
                      const add = item.type === "rent" ? addToRentCart : addToBuyCart;

                      const handleRemoveItem = () => {
                        dispatch(removeAction(key));
                        toast.success("Removed from cart");
                      };

                      const isRent = item.type === "rent";
                      const tagText = isRent ? (item.selectedPackage?.package_name?.toUpperCase() || "RENTAL") : "BUY";
                      const tagDesc = isRent ? `${item.selectedPackage?.package_name || "Rental"} Package` : "Outright Purchase";
                      let duration = "-";
                      if (isRent) {
                        const count = Number(item.duration ?? item.duration_value ?? item.selectedPackage?.duration_value ?? 1) || 1;
                        const planKey = (item.rentalPlan || item.selectedPackage?.package_name || item.package_name || "Daily").toLowerCase();
                        let unit = "day";
                        let plural = "days";
                        if (planKey.includes("week")) { unit = "week"; plural = "weeks"; }
                        else if (planKey.includes("month")) { unit = "month"; plural = "months"; }
                        else if (planKey.includes("annu") || planKey.includes("year")) { unit = "year"; plural = "years"; }
                        duration = `${count} ${count === 1 ? unit : plural}`;
                      }

                      return (
                        <div className="cart-row-item" key={`${item.type}-${key}`}>
                          <div className="row-color-bar" style={{ backgroundColor: isRent ? "#5A67D8" : "#48BB78" }}></div>
                          <div className="row-content">
                            <div className="row-col row-type" style={{ width: !isRent ? "25%" : undefined }}>
                              <span className={`tag ${item.type}`}>{tagText}</span>
                              <p className="type-desc">{tagDesc}</p>
                            </div>
                            {isRent && (
                              <>
                                <div className="row-col row-date">
                                  <span className="col-label">DATE</span>
                                  <p className="col-value">{item.fromDate || item.startDate || item.rental_start_date || "-"}</p>
                                </div>
                                <div className="row-col row-duration">
                                  <span className="col-label">DURATION</span>
                                  <p className="col-value">{duration}</p>
                                </div>
                              </>
                            )}
                            <div className="row-col row-qty">
                              <span className="col-label">QTY</span>
                              {isRent ? (
                                <span className="qty-value" style={{ fontWeight: 600, fontSize: "14px", color: "#111", paddingLeft: "4px" }}>{item.qty || 1}</span>
                              ) : (
                                <div className="qty-wrapper">
                                  <button className="qty-minus" type="button" onClick={() => dispatch(decrease(key))}><FiMinus /></button>
                                  <span className="qty-value">{item.qty || 1}</span>
                                  <button className="qty-plus" type="button" onClick={() => { dispatch(add(item)); toast.success("Quantity updated"); }}><FiPlus /></button>
                                </div>
                              )}
                            </div>
                            <div className="row-col row-price">
                              <span className="col-label" style={{ textAlign: "left", width: "100%" }}>TOTAL</span>
                              {(() => {
                                const qty = item.qty || 1;
                                const currentUnitPrice = getLinePrice(item);
                                const rawOldPrice = getOriginalPrice(item);
                                const currentTotal = currentUnitPrice * qty;
                                const oldTotal = rawOldPrice * qty;
                                const discountPct = item.selectedPackage?.discount_percent
                                  ? Math.round(Number(item.selectedPackage.discount_percent))
                                  : (oldTotal > currentTotal + 0.01 ? Math.round(((oldTotal - currentTotal) / oldTotal) * 100) : 0);

                                return (
                                  <div className="price-display-container" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                                    {discountPct > 0 && oldTotal > currentTotal + 0.01 && (
                                      <div className="discount-price-badge" style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
                                        <span className="strike-old-val" style={{ textDecoration: "line-through", color: "#a0aec0", fontSize: "13px", fontWeight: 500 }}>
                                          {formatMoney(oldTotal)}
                                        </span>
                                        <span className="discount-pill-tag" style={{ backgroundColor: "#e6fffa", color: "#319795", padding: "1px 6px", borderRadius: "4px", fontSize: "11px", fontWeight: 700 }}>
                                          -{discountPct}%
                                        </span>
                                      </div>
                                    )}
                                    <p className="price-val" style={{ margin: 0, fontWeight: 700, color: discountPct > 0 ? "#1a202c" : "#2d3748" }}>
                                      {formatMoney(currentTotal)}
                                    </p>
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="row-col row-actions">
                              <button className="action-btn delete-btn" onClick={handleRemoveItem}>
                                <FiTrash2 />
                              </button>
                            </div>

                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside className="cart-right">
            <div className="order-summary-box">
              <h3 className="order-summary-title" style={{ marginBottom: "10px" }}>Order Summary</h3>
              <div className="summary-total" style={{ marginBottom: "20px" }}>
                <span className="summary-total-label">Grand total</span>
                <span className="summary-total-val">{formatMoney(totalAmount)}</span>
              </div>

              <button className="checkout-btn" type="button" onClick={handleCheckoutClick}>
                <span>Proceed to checkout &rarr;</span>
              </button>

              <div className="secure-checkout" style={{ marginTop: "16px" }}>
                <FiLock /> Secure checkout
              </div>
            </div>
          </aside>
        </div>
      ) : (
        <section className="empty-cart-container empty-cart-simple">
          <div className="empty-cart-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </div>
          <h2 className="empty-cart-simple-title">Your cart is empty</h2>
          <p className="empty-cart-simple-desc">Looks like you haven't added anything to your cart yet.</p>
          <div className="empty-cart-buttons-simple">
            <button className="simple-btn" type="button" onClick={() => navigate("/buy")}>
              Buy Now
            </button>
            <button className="simple-btn simple-btn-outline" type="button" onClick={() => navigate("/rent")}>
              Rent Now
            </button>
          </div>
        </section>
      )}
      <LoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      <AddressModal
        show={showAddressModal}
        onHide={() => setShowAddressModal(false)}
        addressType="both"
        onSuccess={(addr) => {
          setShowAddressModal(false);
          setRefreshAdd(prev => !prev);
        }}
      />
    </main>
  );
}

export default Cartpage;
